import React, { useCallback, useState } from 'react'
import {
  ControlledCheckbox,
  ControlledHiddenInput,
  ControlledSelect,
} from 'transfix-form/components'
import ControlledAutocompleteCarrier from 'transfix-form/components/ControlledAutocompleteCarrier'
import ControlledAutocompleteShipper from 'transfix-form/components/ControlledAutocompleteShipper'
import { Button, ButtonGroup } from 'transfix-ui/components'
import { Box, Typography } from 'transfix-ui/core'
import { IConnectionConfigEnvironment, IConnectionTypes } from 'types/graphqlTypes'
import { getErrorMessage } from 'apps/edi-tools/utils'
import { As2ConnectionConfigGrid } from '../../ConnectionConfigGrids/As2ConnectionConfigGrid'
import { FtpConnectionConfigGrid } from '../../ConnectionConfigGrids/FtpConnectionConfigGrid'
import { useAddInboundConnectionConfigModalFormContext } from './useAddInboundConnectionConfigModalForm'

const CONNECTION_TYPE_OPTIONS = [
  {
    label: 'SFTP with Private Key',
    value: IConnectionTypes.SFTP_WITH_PRIVATE_KEY,
  },
  {
    label: 'SFTP with Password',
    value: IConnectionTypes.SFTP_WITH_PASSWORD,
  },
  {
    label: 'FTP',
    value: IConnectionTypes.FTP,
  },
  {
    label: 'AS2',
    value: IConnectionTypes.AS2,
  },
]

const ENVIRONMENT_OPTIONS = [
  {
    label: 'Production',
    value: IConnectionConfigEnvironment.production,
  },
  {
    label: 'Staging',
    value: IConnectionConfigEnvironment.staging,
  },
  {
    label: 'Development',
    value: IConnectionConfigEnvironment.development,
  },
]

export const AddInboundConnectionConfigModalForm = () => {
  const [connectionType, setConnectionType] = useState<IConnectionTypes>(
    IConnectionTypes.SFTP_WITH_PRIVATE_KEY
  )
  const [environment, setEnvironment] = useState<IConnectionConfigEnvironment>(
    IConnectionConfigEnvironment.staging
  )

  const { control, errors } = useAddInboundConnectionConfigModalFormContext()

  const isAs2 = [connectionType, control.defaultValuesRef.current.connectionType].includes(
    IConnectionTypes.AS2
  )

  const getSearchType = () => {
    const formValue = control.defaultValuesRef.current.company?.data?.__typename
    const normalizedFormValue = formValue === 'CarrierApplication' ? 'carrier' : 'shipper'

    return normalizedFormValue || 'shipper'
  }

  const [searchType, setSearchType] = useState(getSearchType())

  const connectionChangeHandler = useCallback(({ target }) => {
    const val = target.value || {}

    setConnectionType(val)
  }, [])

  const environmentChangeHandler = useCallback(({ target }) => {
    const val = target.value || {}

    setEnvironment(val)
  }, [])

  return (
    <form data-test='AddInboundConnectionConfigModalForm'>
      <div className='mb4 mt2'>
        <ControlledHiddenInput name='id' control={control} />
        <ControlledSelect
          control={control}
          name='connectionType'
          defaultValue={connectionType}
          onChange={connectionChangeHandler}
          SelectProps={{
            id: 'connectionType',
            dataTest: 'connection-type-single-select',
            label: 'Connection Type',
            options: CONNECTION_TYPE_OPTIONS,
            value: connectionType,
            error: !!getErrorMessage(errors, 'connectionType'),
            validationMessage: getErrorMessage(errors, 'connectionType'),
          }}
        />
      </div>
      <ButtonGroup
        className='mb2'
        value={searchType}
        hideLabel
        onChange={(value: string) => {
          setSearchType(value)
        }}>
        <Button value='shipper'>Shipper</Button>
        <Button value='carrier'>Carrier</Button>
      </ButtonGroup>
      {searchType === 'shipper' ? (
        <ControlledAutocompleteShipper
          control={control}
          name='company'
          AutocompleteShipperProps={{
            inputError: !!getErrorMessage(errors, 'company'),
            inputValidationMessage: getErrorMessage(errors, 'company'),
            inputIcon: 'Search',
            renderInputType: 'withIcon',
            placeholder: 'Search',
            dataTest: 'search-input',
            label: 'Shipper name',
            size: 'small',
          }}
        />
      ) : (
        <ControlledAutocompleteCarrier
          control={control}
          name='company'
          AutocompleteCarrierProps={{
            inputError: !!getErrorMessage(errors, 'company'),
            inputValidationMessage: getErrorMessage(errors, 'company'),
            inputIcon: 'Search',
            renderInputType: 'withIcon',
            placeholder: 'Search',
            dataTest: 'search-input',
            label: 'Carrier name',
            size: 'small',
          }}
        />
      )}
      <Box mt={2} mb={2}>
        <ControlledCheckbox
          name='disabled'
          control={control}
          defaultValue={false}
          CheckboxProps={{
            label: <Typography variant='subtitle2'>Disabled</Typography>,
          }}
        />
        <ControlledCheckbox
          name='send997s'
          control={control}
          defaultValue
          CheckboxProps={{
            label: <Typography variant='subtitle2'>Send 997s</Typography>,
          }}
        />
      </Box>
      <ControlledSelect
        control={control}
        name='environment'
        defaultValue={IConnectionConfigEnvironment.staging}
        onChange={environmentChangeHandler}
        SelectProps={{
          dataTest: 'environment-single-select',
          label: 'Environment',
          options: ENVIRONMENT_OPTIONS,
          value: environment,
          error: !!getErrorMessage(errors, 'environment'),
          validationMessage: getErrorMessage(errors, 'environment'),
        }}
      />
      {isAs2 ? (
        <As2ConnectionConfigGrid includeFilenameGlob control={control} errors={errors} />
      ) : (
        <FtpConnectionConfigGrid includeFilenameGlob control={control} errors={errors} />
      )}
    </form>
  )
}
